import { supabase } from '../lib/supabase';

/**
 * Audit Log Service
 * Manages comprehensive audit trail tracking across all system activities
 */

/**
 * Fetch consolidated audit logs from multiple sources
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Array>} Consolidated audit logs
 */
export const fetchAuditLogs = async (filters = {}) => {
  try {
    const {
      startDate,
      endDate,
      eventType,
      severity,
      userId,
      searchQuery
    } = filters;

    // Fetch application logs (admin actions, system activities)
    const appLogsQuery = supabase?.from('application_logs')?.select(`
        *,
        user_profiles!user_id(full_name, email, role)
      `)?.order('created_at', { ascending: false });

    if (startDate) appLogsQuery?.gte('created_at', startDate);
    if (endDate) appLogsQuery?.lte('created_at', endDate);
    if (severity) appLogsQuery?.eq('level', severity);
    if (userId) appLogsQuery?.eq('user_id', userId);
    if (searchQuery) appLogsQuery?.ilike('description', `%${searchQuery}%`);

    const { data: appLogs, error: appLogsError } = await appLogsQuery;
    if (appLogsError) throw appLogsError;

    // Fetch 2FA events
    const twoFAQuery = supabase?.from('two_factor_auth_sessions')?.select(`
        *,
        user_profiles!user_id(full_name, email, role)
      `)?.order('created_at', { ascending: false });

    if (startDate) twoFAQuery?.gte('created_at', startDate);
    if (endDate) twoFAQuery?.lte('created_at', endDate);
    if (userId) twoFAQuery?.eq('user_id', userId);

    const { data: twoFALogs, error: twoFAError } = await twoFAQuery;
    if (twoFAError) throw twoFAError;

    // Fetch compliance actions (permission changes)
    const complianceQuery = supabase?.from('compliance_actions')?.select(`
        *,
        user_profiles!user_id(full_name, email, role),
        compliance_documents(title, document_type)
      `)?.order('created_at', { ascending: false });

    if (startDate) complianceQuery?.gte('created_at', startDate);
    if (endDate) complianceQuery?.lte('created_at', endDate);
    if (userId) complianceQuery?.eq('user_id', userId);

    const { data: complianceLogs, error: complianceError } = await complianceQuery;
    if (complianceError) throw complianceError;

    // Transform and consolidate logs
    const consolidatedLogs = [
      ...transformAppLogs(appLogs || []),
      ...transformTwoFALogs(twoFALogs || []),
      ...transformComplianceLogs(complianceLogs || [])
    ];

    // Apply event type filter
    let filteredLogs = consolidatedLogs;
    if (eventType && eventType !== 'all') {
      filteredLogs = consolidatedLogs?.filter(log => log?.eventType === eventType);
    }

    // Sort by timestamp descending
    filteredLogs?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return filteredLogs;
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

/**
 * Transform application logs to unified format
 */
const transformAppLogs = (logs) => {
  return logs?.map(log => ({
    id: log?.id,
    timestamp: log?.created_at,
    eventType: mapCategoryToEventType(log?.category),
    category: log?.category,
    action: log?.action,
    description: log?.description,
    severity: log?.level,
    userName: log?.user_profiles?.full_name || 'Unknown',
    userEmail: log?.user_profiles?.email || '',
    userRole: log?.user_profiles?.role || '',
    userId: log?.user_id,
    ipAddress: log?.ip_address,
    userAgent: log?.user_agent,
    resourceType: log?.resource_type,
    resourceId: log?.resource_id,
    metadata: log?.metadata,
    source: 'application_logs'
  }));
};

/**
 * Transform 2FA logs to unified format
 */
const transformTwoFALogs = (logs) => {
  return logs?.map(log => ({
    id: log?.id,
    timestamp: log?.created_at,
    eventType: '2fa_events',
    category: 'authentication',
    action: log?.verified ? '2fa_success' : '2fa_attempt',
    description: log?.verified 
      ? `Two-factor authentication successful for ${log?.user_profiles?.full_name}`
      : `Two-factor authentication attempt by ${log?.user_profiles?.full_name}`,
    severity: log?.verified ? 'info' : 'warning',
    userName: log?.user_profiles?.full_name || 'Unknown',
    userEmail: log?.user_profiles?.email || '',
    userRole: log?.user_profiles?.role || '',
    userId: log?.user_id,
    ipAddress: log?.ip_address,
    userAgent: log?.user_agent,
    metadata: {
      verified: log?.verified,
      verified_at: log?.verified_at,
      expires_at: log?.expires_at,
      session_token: log?.session_token?.substring(0, 10) + '...'
    },
    source: 'two_factor_auth_sessions'
  }));
};

/**
 * Transform compliance logs to unified format
 */
const transformComplianceLogs = (logs) => {
  return logs?.map(log => ({
    id: log?.id,
    timestamp: log?.created_at,
    eventType: 'permission_changes',
    category: 'compliance',
    action: log?.action_type,
    description: generateComplianceDescription(log),
    severity: getSeverityFromActionType(log?.action_type),
    userName: log?.user_profiles?.full_name || 'Unknown',
    userEmail: log?.user_profiles?.email || '',
    userRole: log?.user_profiles?.role || '',
    userId: log?.user_id,
    metadata: {
      document_title: log?.compliance_documents?.title,
      document_type: log?.compliance_documents?.document_type,
      previous_status: log?.previous_status,
      new_status: log?.new_status,
      notes: log?.notes,
      ...log?.metadata
    },
    source: 'compliance_actions'
  }));
};

/**
 * Map category to event type
 */
const mapCategoryToEventType = (category) => {
  const mapping = {
    'authentication': 'admin_actions',
    'employee_management': 'admin_actions',
    'task_management': 'system_activities',
    'ipm_operations': 'system_activities',
    'system': 'system_activities'
  };
  return mapping?.[category] || 'system_activities';
};

/**
 * Generate human-readable description for compliance actions
 */
const generateComplianceDescription = (log) => {
  const user = log?.user_profiles?.full_name || 'Unknown user';
  const document = log?.compliance_documents?.title || 'document';
  const actionMap = {
    'review': `reviewed ${document}`,
    'approve': `approved ${document}`,
    'reject': `rejected ${document}`,
    'update': `updated ${document}`,
    'archive': `archived ${document}`,
    'flag': `flagged ${document}`
  };
  return `${user} ${actionMap?.[log?.action_type] || 'performed action on ' + document}`;
};

/**
 * Get severity level from action type
 */
const getSeverityFromActionType = (actionType) => {
  const severityMap = {
    'approve': 'info',
    'review': 'info',
    'update': 'info',
    'reject': 'warning',
    'flag': 'warning',
    'archive': 'info'
  };
  return severityMap?.[actionType] || 'info';
};

/**
 * Fetch audit log statistics
 */
export const fetchAuditLogStats = async (filters = {}) => {
  try {
    const logs = await fetchAuditLogs(filters);
    
    const stats = {
      total: logs?.length,
      byEventType: {},
      bySeverity: {},
      byCategory: {},
      recentCritical: logs?.filter(log => log?.severity === 'critical')?.slice(0, 10),
      topUsers: {}
    };

    logs?.forEach(log => {
      // Count by event type
      stats.byEventType[log.eventType] = (stats?.byEventType?.[log?.eventType] || 0) + 1;
      
      // Count by severity
      stats.bySeverity[log.severity] = (stats?.bySeverity?.[log?.severity] || 0) + 1;
      
      // Count by category
      stats.byCategory[log.category] = (stats?.byCategory?.[log?.category] || 0) + 1;
      
      // Count by user
      if (log?.userName) {
        stats.topUsers[log.userName] = (stats?.topUsers?.[log?.userName] || 0) + 1;
      }
    });

    return stats;
  } catch (error) {
    console.error('Error fetching audit log stats:', error);
    throw error;
  }
};

/**
 * Fetch unique users for filtering
 */
export const fetchAuditUsers = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, role')?.order('full_name');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching audit users:', error);
    throw error;
  }
};

/**
 * Export audit logs (for CSV/PDF generation)
 */
export const exportAuditLogs = async (filters = {}) => {
  try {
    const logs = await fetchAuditLogs(filters);
    
    return logs?.map(log => ({
      timestamp: new Date(log.timestamp)?.toLocaleString(),
      eventType: formatEventType(log?.eventType),
      severity: log?.severity?.toUpperCase(),
      action: log?.action,
      description: log?.description,
      user: log?.userName,
      userEmail: log?.userEmail,
      userRole: log?.userRole,
      ipAddress: log?.ipAddress || 'N/A',
      category: log?.category,
      source: log?.source
    }));
  } catch (error) {
    console.error('Error exporting audit logs:', error);
    throw error;
  }
};

/**
 * Format event type for display
 */
const formatEventType = (eventType) => {
  const typeMap = {
    'admin_actions': 'Admin Actions',
    '2fa_events': '2FA Events',
    'permission_changes': 'Permission Changes',
    'system_activities': 'System Activities'
  };
  return typeMap?.[eventType] || eventType;
};